import { useRef, useState, type ChangeEvent, type ClipboardEvent, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ImagePlus, Plus, Trash2, X } from 'lucide-react'
import { Avatar } from '@/components/Avatar'
import { ReleaseBody, ReleaseCard } from '@/components/releases/ReleaseView'
import { GameServersAnnouncementCard } from '@/components/releases/GameServersAnnouncement'
import { GAME_SERVERS_RELEASE_ID } from '@/lib/gameServersAnnouncement'
import { ApiError } from '@/lib/api'
import { describeError } from '@/lib/auth'
import { avatarUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import { copyForTelegram, fitReleaseImage, RELEASE_LIMITS, releaseDate, releaseImageUrl, releasesApi, type ReleaseInput, type ReleaseItem, type StaffRelease } from '@/lib/releasesApi'
import { useAuthStore } from '@/store/auth'
import auth from '@/pages/AuthPage.module.css'
import { Modal } from './Modal'
import layer from './SettingsLayer.module.css'
import styles from './ReleasesSection.module.css'

type Status = 'draft' | 'live' | 'down'
const statusOf = (r: StaffRelease): Status => (!r.published_at ? 'draft' : r.unpublished_at ? 'down' : 'live')
const STATUS_LABEL: Record<Status, string> = { draft: 'Черновик', live: 'Опубликован', down: 'Снят' }

const num = (n: number) => n.toLocaleString('ru-RU')
const blank = (): ReleaseInput => ({ title: '', items: [{ text: '' }, { text: '' }, { text: '' }] })

/** Что уйдёт на сервер: без пробелов по краям и без пустых пунктов. */
function clean(d: ReleaseInput): ReleaseInput {
  return {
    title: d.title.trim(),
    items: d.items.map((i) => ({ text: i.text.trim(), ...(i.image ? { image: i.image } : {}) })).filter((i) => i.text || i.image),
  }
}

/** Что мешает сохранить: поле → подсказка. Номера пунктов — как в редакторе, с пустыми. */
function problems(d: ReleaseInput): Record<string, string> {
  const out: Record<string, string> = {}
  const title = d.title.trim()
  if (!title) out.title = 'Нужен заголовок'
  else if ([...title].length > RELEASE_LIMITS.title) out.title = `Заголовок до ${RELEASE_LIMITS.title} символов`
  let filled = 0
  d.items.forEach((it, i) => {
    const text = it.text.trim()
    if (text || it.image) filled++
    if (!text && it.image) out[`items.${i}`] = 'У пункта с картинкой нужен текст'
    else if ([...text].length > RELEASE_LIMITS.text) out[`items.${i}`] = `Пункт до ${RELEASE_LIMITS.text} символов`
  })
  if (filled === 0) out.items = 'Нужен хотя бы один пункт'
  else if (filled > RELEASE_LIMITS.items) out.items = `Не больше ${RELEASE_LIMITS.items} пунктов`
  return out
}

type Flash = { ok: boolean; text: string } | null

/**
 * Настройки → Персоналу → «Что нового». Выпуск пишет человек: заголовок и 3–5 пунктов в разметке
 * чата, у пункта может быть картинка. Предпросмотр — карточка и окно в обеих темах, как их увидят
 * люди. Публикация: карточкой в приложении и, по желанию, тем же текстом в #обновления от «Кличат».
 */
export function ReleasesSection() {
  const list = useQuery({ queryKey: ['releases', 'staff'], queryFn: releasesApi.staff })
  const releases = list.data?.releases ?? []
  const [selected, setSelected] = useState<string | null>(null)
  const [flash, setFlash] = useState<Flash>(null)
  const current = selected && selected !== 'new' ? (releases.find((r) => r.id === selected) ?? null) : null
  const pick = (id: string | null) => {
    setSelected(id)
    setFlash(null)
  }

  return (
    <section>
      <h1 className={layer.sectionTitle}>Что нового</h1>
      <p className={layer.text}>Выпуск пишете вы: заголовок и 3–5 пунктов.</p>
      <ul className={layer.points}>
        <li>люди увидят карточку внизу слева, над своей панелью; она не звучит и не шлёт уведомлений</li>
        <li>открыли или закрыли — карточка пропала на всех устройствах</li>
        <li>новый выпуск заменяет старую карточку, история остаётся в «Поддержке и новостях»</li>
      </ul>

      <h2 className={layer.subTitle}>Выпуски</h2>
      {list.isPending ? (
        <p className={styles.muted}>Загружаем…</p>
      ) : list.isError ? (
        <p className={styles.muted}>Не удалось загрузить выпуски: {describeError(list.error).message}</p>
      ) : (
        <>
          {releases.length === 0 ? <p className={styles.muted}>Выпусков ещё нет.</p> : (
            <ul className={styles.list}>
              {releases.map((r) => {
                const st = statusOf(r)
                return (
                  <li key={r.id}>
                    <button type="button" className={cx(styles.row, selected === r.id && styles.rowActive)} onClick={() => pick(r.id)} aria-current={selected === r.id ? 'true' : undefined}>
                      <span className={styles.rowMain}>
                        <span className={styles.rowTitle}>{r.title}</span>
                        <span className={styles.rowMeta}>
                          {r.id === GAME_SERVERS_RELEASE_ID ? 'Отдельная плашка · ' : ''}
                          {st === 'draft' ? `изменён ${releaseDate(r.updated_at)}` : st === 'down' ? `снят ${releaseDate(r.unpublished_at)}` : `${releaseDate(r.published_at)} · открыли ${num(r.opened)} из ${num(r.audience)}`}
                        </span>
                      </span>
                      <span className={styles.pill} data-status={st}>{STATUS_LABEL[st]}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          <div className={styles.listActions}>
            <button type="button" className={cx(layer.button, styles.iconButton)} onClick={() => pick('new')} disabled={selected === 'new'}>
              <Plus size={16} aria-hidden /> Новый выпуск
            </button>
          </div>
        </>
      )}

      {selected ? (
        <Editor key={selected} release={current} flash={flash} setFlash={setFlash} onSelect={setSelected} onClose={() => pick(null)} />
      ) : flash ? (
        <p className={cx(styles.flash, !flash.ok && styles.flashError)} role={flash.ok ? 'status' : 'alert'}>{flash.text}</p>
      ) : null}
    </section>
  )
}

interface EditorProps {
  /** null — новый выпуск, ещё не сохранён. */
  release: StaffRelease | null
  flash: Flash
  setFlash: (f: Flash) => void
  /** Выпуск сохранён впервые (у него появился id) или удалён. */
  onSelect: (id: string | null) => void
  onClose: () => void
}

function Editor({ release, flash, setFlash, onSelect, onClose }: EditorProps) {
  const isGameAnnouncement = release?.id === GAME_SERVERS_RELEASE_ID
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [draft, setDraft] = useState<ReleaseInput>(() => (release ? { title: release.title, items: release.items.map((i) => ({ ...i })) } : blank()))
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState<number | null>(null)
  const [card, setCard] = useState(release?.card ?? true)
  const [postOfficial, setPostOfficial] = useState(!isGameAnnouncement)
  const [confirm, setConfirm] = useState<null | 'publish' | 'unpublish' | 'delete'>(null)
  const [copied, setCopied] = useState(false)
  const [theme, setTheme] = useState<'dark' | 'light'>('dark')
  const fileRef = useRef<HTMLInputElement>(null)
  const pickFor = useRef(0)

  const status: Status = release ? statusOf(release) : 'draft'
  const cleaned = clean(draft)
  const dirty = !release || JSON.stringify(cleaned) !== JSON.stringify(clean({ title: release.title, items: release.items }))
  const issues = problems(draft)
  const valid = Object.keys(issues).length === 0
  const filled = cleaned.items.length

  const setItem = (i: number, patch: Partial<ReleaseItem>) => setDraft((d) => ({ ...d, items: d.items.map((it, n) => (n === i ? { ...it, ...patch } : it)) }))
  const dropImage = (i: number) => setDraft((d) => ({ ...d, items: d.items.map((it, n) => (n === i ? { text: it.text } : it)) }))
  const move = (i: number, by: -1 | 1) =>
    setDraft((d) => {
      const items = [...d.items]
      const j = i + by
      if (j < 0 || j >= items.length) return d
      ;[items[i], items[j]] = [items[j] as ReleaseItem, items[i] as ReleaseItem]
      return { ...d, items }
    })
  const removeItem = (i: number) => setDraft((d) => ({ ...d, items: d.items.filter((_, n) => n !== i) }))
  const addItem = () => setDraft((d) => ({ ...d, items: [...d.items, { text: '' }] }))

  const upload = async (i: number, file: File) => {
    if (!file.type.startsWith('image/')) {
      setFlash({ ok: false, text: 'Нужна картинка: PNG, JPEG, WebP или GIF' })
      return
    }
    setUploading(i)
    setFlash(null)
    try {
      const { name } = await releasesApi.uploadImage(await fitReleaseImage(file))
      setItem(i, { image: name })
    } catch (err) {
      setFlash({ ok: false, text: err instanceof ApiError || !(err instanceof Error) ? describeError(err).message : err.message })
    } finally {
      setUploading(null)
    }
  }
  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) void upload(pickFor.current, file)
  }
  // скриншот из буфера — сразу картинкой пункта
  const onPaste = (i: number, e: ClipboardEvent<HTMLTextAreaElement>) => {
    const file = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'))
    if (!file) return
    e.preventDefault()
    void upload(i, file)
  }

  /** Сохраняет, если есть что, и возвращает свежую версию выпуска. */
  const persist = async (): Promise<StaffRelease> => {
    if (release && !dirty) return release
    return release ? releasesApi.update(release.id, cleaned) : releasesApi.create(cleaned)
  }

  /** postOnly — действие только про пост в канал: его ошибка и есть весь итог. */
  const run = async (fn: () => Promise<StaffRelease | null>, done: string, postOnly = false) => {
    setBusy(true)
    setFlash(null)
    try {
      const r = await fn()
      // и список в панели, и история, и своя карточка: после публикации она появится и у автора
      await qc.invalidateQueries({ queryKey: ['releases'] })
      if (r?.official_error) setFlash({ ok: false, text: postOnly ? r.official_error : `${done}, но есть проблема. ${r.official_error}` })
      else setFlash({ ok: true, text: done })
      if (r && r.id !== release?.id) onSelect(r.id)
    } catch (err) {
      setFlash({ ok: false, text: describeError(err).message })
      // черновик мог успеть сохраниться до ошибки публикации — пусть он будет виден в списке
      void qc.invalidateQueries({ queryKey: ['releases', 'staff'] })
    } finally {
      setBusy(false)
    }
  }

  const save = () => void run(persist, status === 'live' ? 'Правка сохранена — её уже видят все' : 'Черновик сохранён')
  const publish = () => {
    setConfirm(null)
    void run(async () => releasesApi.publish((await persist()).id, { card: isGameAnnouncement || card, post_official: !isGameAnnouncement && postOfficial }), isGameAnnouncement ? 'Плашка опубликована' : 'Выпуск опубликован')
  }
  const unpublish = () => {
    setConfirm(null)
    if (release) void run(() => releasesApi.unpublish(release.id), 'Выпуск снят с публикации')
  }
  const remove = () => {
    setConfirm(null)
    if (!release) return
    void run(async () => {
      await releasesApi.remove(release.id)
      onSelect(null)
      return null
    }, 'Выпуск удалён')
  }
  const copy = async () => {
    try {
      await copyForTelegram(cleaned)
      setFlash(null)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setFlash({ ok: false, text: 'Браузер не дал доступ к буферу обмена — разрешите его и попробуйте ещё раз' })
    }
  }

  const name = user?.global_name ?? user?.username ?? 'Вы'
  const previewTitle = cleaned.title || 'Заголовок выпуска'
  const previewItems = cleaned.items.length ? cleaned.items : [{ text: 'Здесь будут пункты выпуска.' }]

  return (
    <div className={styles.editor}>
      <div className={styles.editorHead}>
        <h2 className={layer.subTitle}>{isGameAnnouncement ? 'Игровые серверы · отдельная плашка' : release ? 'Редактор' : 'Новый выпуск'}</h2>
        <button type="button" className={styles.closeEditor} onClick={onClose} aria-label="Закрыть редактор" data-tooltip="Закрыть редактор"><X size={18} /></button>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="release-title">Заголовок</label>
        <input
          id="release-title"
          className={auth.input}
          value={draft.title}
          maxLength={RELEASE_LIMITS.title + 20}
          placeholder="Например: Голос без шума и стримы в 4K"
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
        />
        <Hint left={RELEASE_LIMITS.title - [...draft.title.trim()].length} error={draft.title ? issues.title : undefined} />
      </div>

      {!isGameAnnouncement ? <div className={styles.field}>
        <span className={styles.label}>Пункты</span>
        <ul className={cx(layer.points, styles.help)}>
          <li>разметка как в чате: **жирный**, *курсив*, `код`, ссылки</li>
          <li>начало пункта жирным с точкой, например **Статусы.**, в посте встаёт подзаголовком</li>
          <li>к пункту можно приложить картинку — кнопкой или вставив скриншот прямо в поле</li>
        </ul>
        <ol className={styles.items}>
          {draft.items.map((it, i) => (
            <li key={i} className={styles.item}>
              <span className={styles.itemNum} aria-hidden>{i + 1}</span>
              <div className={styles.itemBody}>
                <textarea
                  className={cx(auth.input, styles.itemText)}
                  value={it.text}
                  rows={2}
                  maxLength={RELEASE_LIMITS.text + 100}
                  aria-label={`Пункт ${i + 1}`}
                  placeholder={i === 0 ? 'Что появилось и зачем это человеку' : 'Ещё одно изменение'}
                  onChange={(e) => setItem(i, { text: e.target.value })}
                  onPaste={(e) => onPaste(i, e)}
                />
                <div className={styles.itemTools}>
                  {it.image ? (
                    <span className={styles.thumb}>
                      <img src={releaseImageUrl(it.image)} alt="" />
                      <button type="button" className={styles.thumbDrop} onClick={() => dropImage(i)} aria-label={`Убрать картинку пункта ${i + 1}`} data-tooltip="Убрать картинку"><X size={14} /></button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={styles.tool}
                      disabled={uploading !== null}
                      onClick={() => {
                        pickFor.current = i
                        fileRef.current?.click()
                      }}
                    >
                      <ImagePlus size={16} aria-hidden /> {uploading === i ? 'Загружаем…' : 'Картинка'}
                    </button>
                  )}
                  <span className={styles.toolsGap} />
                  <button type="button" className={styles.tool} onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Поднять пункт ${i + 1}`} data-tooltip="Выше"><ArrowUp size={16} /></button>
                  <button type="button" className={styles.tool} onClick={() => move(i, 1)} disabled={i === draft.items.length - 1} aria-label={`Опустить пункт ${i + 1}`} data-tooltip="Ниже"><ArrowDown size={16} /></button>
                  <button type="button" className={styles.tool} onClick={() => removeItem(i)} disabled={draft.items.length === 1} aria-label={`Удалить пункт ${i + 1}`} data-tooltip="Удалить пункт"><Trash2 size={16} /></button>
                </div>
                <Hint left={RELEASE_LIMITS.text - [...it.text.trim()].length} error={issues[`items.${i}`]} quietAbove={120} />
              </div>
            </li>
          ))}
        </ol>
        <div className={styles.itemsFoot}>
          <button type="button" className={cx(layer.button, layer.buttonSecondary, styles.iconButton)} onClick={addItem} disabled={draft.items.length >= RELEASE_LIMITS.items}>
            <Plus size={16} aria-hidden /> Добавить пункт
          </button>
          <span className={cx(styles.hint, issues.items && styles.hintError)}>
            {issues.items ?? (filled < 3 || filled > 5 ? `Пунктов: ${filled}. Лучше всего читается 3–5` : `Пунктов: ${filled}`)}
          </span>
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={onFile} />
      </div> : null}

      <div className={layer.buttonRow}>
        <button type="button" className={cx(layer.button, status === 'live' ? null : layer.buttonSecondary)} disabled={busy || !valid || !dirty} onClick={save}>
          {status === 'live' ? 'Сохранить правки' : status === 'down' ? 'Сохранить' : 'Сохранить черновик'}
        </button>
        {!isGameAnnouncement ? <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={!cleaned.title && filled === 0} onClick={() => void copy()}>
          {copied ? 'Скопировано' : 'Скопировать для Telegram'}
        </button> : null}
      </div>
      {status === 'live' && dirty && valid ? <p className={styles.help}>Правка сразу видна всем{release?.official ? ', пост в #обновления поправится тоже' : ''}.</p> : null}

      <h2 className={layer.subTitle}>Предпросмотр</h2>
      {isGameAnnouncement ? <>
        <p className={styles.help}>Отдельная плашка сверху справа. Кнопка открывает общий каталог серверов. Основная новость остаётся на месте.</p>
        <div className={styles.stage} inert>
          <GameServersAnnouncementCard title={previewTitle} preview onClose={() => undefined} onExplore={() => undefined} />
        </div>
      </> : <>
      <p className={styles.help}>Карточка внизу слева, над панелью пользователя. Нажатие на неё открывает окно выпуска.</p>
      <div className={styles.panes}>
        {(['dark', 'light'] as const).map((t) => (
          <figure key={t} className={styles.pane} data-theme={t}>
            <figcaption className={styles.paneLabel}>{t === 'dark' ? 'Тёмная тема' : 'Светлая тема'}</figcaption>
            <div className={styles.dock} inert>
              <ReleaseCard title={previewTitle} />
              <div className={styles.panelRow}>
                <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={32} status="online" src={user ? avatarUrl(user.id, user.avatar) : undefined} decoration={user?.avatar_decoration_data} />
                <span className={styles.panelText}>
                  <span className={styles.panelName}>{name}</span>
                  <span className={styles.panelStatus}>В сети</span>
                </span>
              </div>
            </div>
          </figure>
        ))}
      </div>
      <div className={styles.switch} role="radiogroup" aria-label="Тема окна выпуска">
        {(['dark', 'light'] as const).map((t) => (
          <button key={t} type="button" role="radio" aria-checked={theme === t} className={cx(styles.switchItem, theme === t && styles.switchActive)} onClick={() => setTheme(t)}>
            {t === 'dark' ? 'Тёмная' : 'Светлая'}
          </button>
        ))}
      </div>
      <div className={styles.stage} data-theme={theme}>
        <div className={styles.modal} inert>
          <div className={styles.modalHead}>
            <span className={styles.modalTitle}>Что нового</span>
            <span className={styles.modalClose} aria-hidden><X size={22} /></span>
          </div>
          <div className={styles.modalBody}>
            <ReleaseBody title={previewTitle} items={previewItems} publishedAt={release?.published_at ?? null} />
          </div>
        </div>
      </div>
      </>}

      <h2 className={layer.subTitle}>Публикация</h2>
      {release && status !== 'draft' ? <PublishedFacts release={release} /> : null}
      {status === 'live' && release ? (
        <div className={layer.buttonRow}>
          {/* пост не вышел или его не заказывали — выложить можно и потом */}
          {!isGameAnnouncement && !release.official ? (
            <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={busy || dirty} data-tooltip={dirty ? 'Сначала сохраните правки' : undefined} onClick={() => void run(() => releasesApi.publish(release.id, { card: release.card, post_official: true }), 'Пост в #обновления вышел', true)}>
              Выложить в #обновления
            </button>
          ) : null}
          <button type="button" className={cx(layer.button, layer.buttonDanger)} disabled={busy} onClick={() => setConfirm('unpublish')}>Снять с публикации</button>
        </div>
      ) : (
        <>
          {isGameAnnouncement ? <p className={styles.help}>Плашка показывается один раз пользователям, зарегистрированным до её первой публикации.</p> : <><label className={styles.check}>
            <input type="checkbox" checked={card} onChange={(e) => setCard(e.target.checked)} />
            <span>
              <span className={styles.checkTitle}>Показать карточкой в приложении</span>
              <span className={styles.checkText}>У всех, кто зарегистрировался раньше. Без звука и уведомлений, в голосе не показывается.</span>
            </span>
          </label>
          <label className={styles.check}>
            <input type="checkbox" checked={postOfficial} onChange={(e) => setPostOfficial(e.target.checked)} />
            <span>
              <span className={styles.checkTitle}>Выложить в #обновления на официальном сервере</span>
              <span className={styles.checkText}>Тот же текст от имени «Кличат». Правка выпуска поправит пост, снятие — удалит.</span>
            </span>
          </label>
          </>}
          <div className={layer.buttonRow}>
            <button type="button" className={layer.button} disabled={busy || !valid} onClick={() => setConfirm('publish')}>
              {status === 'down' ? 'Опубликовать снова' : 'Опубликовать'}
            </button>
            {release && !isGameAnnouncement ? <button type="button" className={cx(layer.button, layer.buttonDanger)} disabled={busy} onClick={() => setConfirm('delete')}>Удалить</button> : null}
          </div>
          {!valid ? <span className={cx(styles.hint, styles.hintError)}>{Object.values(issues)[0]}</span> : null}
        </>
      )}

      {flash ? <p className={cx(styles.flash, !flash.ok && styles.flashError)} role={flash.ok ? 'status' : 'alert'}>{flash.text}</p> : null}

      {confirm === 'publish' ? (
        <Confirm title={isGameAnnouncement ? 'Опубликовать плашку?' : 'Опубликовать выпуск?'} action={status === 'down' ? 'Опубликовать снова' : 'Опубликовать'} onConfirm={publish} onClose={() => setConfirm(null)}>
          {isGameAnnouncement ? <p>Плашка с логотипами появится сверху справа и откроет каталог серверов. Основная новость останется на месте.</p> : <>
          {card ? <p>Карточку увидят все, кто зарегистрировался до этой минуты: внизу слева, без звука и уведомлений.</p> : <p>Без карточки выпуск появится только в истории «Что нового».</p>}
          {postOfficial ? <p>В #обновления на официальном сервере выйдет пост от имени «Кличат».</p> : null}
          </>}
          {status === 'down' ? <p>Кто уже видел выпуск, карточку снова не получит.</p> : null}
        </Confirm>
      ) : confirm === 'unpublish' ? (
        <Confirm title="Снять с публикации?" action="Снять" danger onConfirm={unpublish} onClose={() => setConfirm(null)}>
          {isGameAnnouncement ? <p>Новые показы плашки прекратятся. Основная новость останется на месте.</p> : <p>Карточка пропадёт, выпуск уйдёт из истории{release?.official ? ', пост в #обновления удалится' : ''}. Вернуть можно кнопкой «Опубликовать снова».</p>}
        </Confirm>
      ) : confirm === 'delete' ? (
        <Confirm title="Удалить выпуск?" action="Удалить" danger onConfirm={remove} onClose={() => setConfirm(null)}>
          <p>{status === 'draft' ? 'Черновик удалится насовсем.' : 'Выпуск и его статистика удалятся насовсем.'}</p>
        </Confirm>
      ) : null}
    </div>
  )
}

/** Опубликован или снят: когда, как показан и сколько людей открыли. */
function PublishedFacts({ release }: { release: StaffRelease }) {
  const isGameAnnouncement = release.id === GAME_SERVERS_RELEASE_ID
  const closed = Math.max(0, release.seen - release.opened)
  const share = release.audience > 0 ? Math.round((release.opened / release.audience) * 100) : 0
  return (
    <div className={styles.facts}>
      <p className={styles.factsLine}>
        {release.unpublished_at ? `Снят ${releaseDate(release.unpublished_at)}, выходил ${releaseDate(release.published_at)}` : `Опубликован ${releaseDate(release.published_at)}`}
        {' · '}
        {isGameAnnouncement ? 'отдельной плашкой с логотипами' : release.card ? 'карточкой в приложении' : 'без карточки'}
        {' · '}
        {release.official ? 'пост в #обновления есть' : 'без поста в #обновления'}
      </p>
      <dl className={styles.stats}>
        <div className={styles.stat}>
          <dt>{isGameAnnouncement ? 'Открыли каталог' : 'Открыли'}</dt>
          <dd>{num(release.opened)}<span className={styles.statNote}>{share}%</span></dd>
        </div>
        <div className={styles.stat}>
          <dt>{isGameAnnouncement ? 'Увидели' : 'Закрыли, не открывая'}</dt>
          <dd>{num(isGameAnnouncement ? release.seen : closed)}</dd>
        </div>
        <div className={styles.stat}>
          <dt>Могли увидеть</dt>
          <dd>{num(release.audience)}</dd>
        </div>
      </dl>
    </div>
  )
}

/** Осталось символов или что не так с полем. Пока запас большой, счётчик не маячит. */
function Hint({ left, error, quietAbove = 40 }: { left: number; error?: string | undefined; quietAbove?: number }) {
  if (error) return <span className={cx(styles.hint, styles.hintError)}>{error}</span>
  if (left > quietAbove) return null
  return <span className={cx(styles.hint, left < 0 && styles.hintError)}>{left < 0 ? `Длиннее на ${-left}` : `Осталось ${left}`}</span>
}

function Confirm({ title, action, danger, onConfirm, onClose, children }: { title: string; action: string; danger?: boolean; onConfirm: () => void; onClose: () => void; children: ReactNode }) {
  return (
    <Modal title={title} onClose={onClose} width={440}>
      <div className={styles.confirmText}>{children}</div>
      <div className={cx(layer.buttonRow, styles.confirmActions)}>
        <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={onClose}>Отмена</button>
        <button type="button" className={cx(layer.button, danger && styles.dangerSolid)} onClick={onConfirm} autoFocus>{action}</button>
      </div>
    </Modal>
  )
}
